import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Camera } from "lucide-react";

export interface ContactPerson {
  id: string;
  name: string;
  role?: string;
  phone?: string;
  email?: string;
  photo_url?: string | null;
  // Local-only: holds the file before upload
  _file?: File | null;
  _previewUrl?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (person: ContactPerson) => void;
  initial?: ContactPerson | null;
}

export function ContactPersonModal({ open, onClose, onSave, initial }: Props) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      setRole(initial?.role ?? "");
      setPhone(initial?.phone ?? "");
      setEmail(initial?.email ?? "");
      setPreview(initial?._previewUrl ?? initial?.photo_url ?? null);
      setFile(initial?._file ?? null);
    }
  }, [open, initial]);

  const handlePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      id: initial?.id ?? crypto.randomUUID(),
      name: name.trim(),
      role: role.trim() || undefined,
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
      photo_url: initial?.photo_url ?? null,
      _file: file,
      _previewUrl: preview,
    });
    onClose();
  };

  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{initial ? "Redigera kontaktperson" : "Lägg till kontaktperson"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="relative h-20 w-20 rounded-full ring-2 ring-border hover:ring-primary transition-all"
            >
              <Avatar className="h-20 w-20">
                {preview ? (
                  <AvatarImage src={preview} alt={name || "Kontaktperson"} />
                ) : (
                  <AvatarFallback>{initials || <Camera className="h-6 w-6 text-muted-foreground" />}</AvatarFallback>
                )}
              </Avatar>
              <span className="absolute bottom-0 right-0 rounded-full bg-primary p-1 text-primary-foreground">
                <Camera className="h-3 w-3" />
              </span>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhoto}
              />
            </button>
          </div>
          <div>
            <Label>Namn *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label>Roll/Titel</Label>
            <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="T.ex. Säljare, VD" />
          </div>
          <div>
            <Label>Telefonnummer</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <Label>E-post</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Avbryt</Button>
            <Button type="submit">Lägg till</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
